import React, { useState, useRef, useEffect, useCallback, useMemo } from "react"
import { useMentionUsers, MentionUser } from "@/hooks/useMentionUsers"

export interface MentionInputProps {
  value: string
  onChange: (value: string, mentionedUserIds: string[]) => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  inputStyle?: React.CSSProperties
  multiline?: boolean
  rows?: number
  id?: string
  autoFocus?: boolean
  customUsers?: MentionUser[]
  showQuickAtButton?: boolean
}

export const MentionInput: React.FC<MentionInputProps> = ({
  value,
  onChange,
  onKeyDown,
  placeholder = "Escribe un mensaje o usa @ para mencionar...",
  disabled = false,
  className = "",
  inputStyle = {},
  multiline = false,
  rows = 1,
  id,
  autoFocus = false,
  customUsers,
  showQuickAtButton = true,
}) => {
  const { users } = useMentionUsers(customUsers)
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null)
  const dropdownRef = useRef<HTMLDivElement | null>(null)

  const [isOpen, setIsOpen] = useState(false)
  const [mentionQuery, setMentionQuery] = useState("")
  const [mentionStartIndex, setMentionStartIndex] = useState<number>(-1)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [mentionedUserIds, setMentionedUserIds] = useState<string[]>([])

  // Filter users based on query
  const filteredUsers = useMemo(() => {
    if (!mentionQuery) return users.slice(0, 8)
    const q = mentionQuery.toLowerCase()
    return users
      .filter((u) => u.name.toLowerCase().includes(q) || (u.role && u.role.toLowerCase().includes(q)))
      .slice(0, 8)
  }, [users, mentionQuery])

  // Keep selected index in bounds
  useEffect(() => {
    setSelectedIndex(0)
  }, [filteredUsers])

  // Detect @ mention trigger from cursor position
  const checkMentionTrigger = useCallback((text: string, cursorPos: number) => {
    const textBeforeCursor = text.slice(0, cursorPos)
    const lastAtIndex = textBeforeCursor.lastIndexOf("@")

    if (lastAtIndex === -1) {
      setIsOpen(false)
      return
    }

    // Ensure @ is at start of line or preceded by whitespace
    const charBeforeAt = lastAtIndex > 0 ? textBeforeCursor[lastAtIndex - 1] : " "
    if (!/\s/.test(charBeforeAt) && lastAtIndex !== 0) {
      setIsOpen(false)
      return
    }

    const query = textBeforeCursor.slice(lastAtIndex + 1)
    // Avoid opening if user typed space or newline after query
    if (query.includes("\n") || query.length > 25) {
      setIsOpen(false)
      return
    }

    setMentionStartIndex(lastAtIndex)
    setMentionQuery(query)
    setIsOpen(true)
  }, [])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const nextVal = e.target.value
    const cursorPos = e.target.selectionEnd || nextVal.length

    // Re-check mentioned user IDs still present in text
    const stillPresentIds = mentionedUserIds.filter((uid) => {
      const u = users.find((usr) => usr.id === uid)
      return u && (nextVal.includes(`@${u.name}`) || nextVal.includes(`@[${u.name}]`))
    })

    setMentionedUserIds(stillPresentIds)
    onChange(nextVal, stillPresentIds)
    checkMentionTrigger(nextVal, cursorPos)
  }

  const handleSelectUser = useCallback(
    (user: MentionUser) => {
      if (mentionStartIndex < 0) return

      const before = value.slice(0, mentionStartIndex)
      const after = value.slice(inputRef.current?.selectionEnd || value.length)
      const mentionToken = `@${user.name} `
      const nextValue = `${before}${mentionToken}${after}`

      const nextIds = Array.from(new Set([...mentionedUserIds, user.id]))
      setMentionedUserIds(nextIds)
      onChange(nextValue, nextIds)

      setIsOpen(false)
      setMentionQuery("")
      setMentionStartIndex(-1)

      // Set focus and reposition cursor
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus()
          const newCursorPos = before.length + mentionToken.length
          inputRef.current.setSelectionRange(newCursorPos, newCursorPos)
        }
      }, 10)
    },
    [mentionStartIndex, value, mentionedUserIds, onChange]
  )

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (isOpen && filteredUsers.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault()
        setSelectedIndex((prev) => (prev + 1) % filteredUsers.length)
        return
      }
      if (e.key === "ArrowUp") {
        e.preventDefault()
        setSelectedIndex((prev) => (prev - 1 + filteredUsers.length) % filteredUsers.length)
        return
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault()
        const selected = filteredUsers[selectedIndex]
        if (selected) {
          handleSelectUser(selected)
        }
        return
      }
      if (e.key === "Escape") {
        e.preventDefault()
        setIsOpen(false)
        return
      }
    }

    if (onKeyDown) {
      onKeyDown(e)
    }
  }

  // Close dropdown on outside click
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false)
      }
    }
    document.addEventListener("mousedown", handleOutsideClick)
    return () => document.removeEventListener("mousedown", handleOutsideClick)
  }, [])

  const triggerAtSymbol = () => {
    if (disabled || !inputRef.current) return
    const curPos = inputRef.current.selectionEnd || value.length
    const before = value.slice(0, curPos)
    const after = value.slice(curPos)
    const needsSpace = before.length > 0 && !/\s$/.test(before)
    const nextVal = `${before}${needsSpace ? " " : ""}@${after}`
    const newPos = curPos + (needsSpace ? 2 : 1)

    onChange(nextVal, mentionedUserIds)
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus()
        inputRef.current.setSelectionRange(newPos, newPos)
        checkMentionTrigger(nextVal, newPos)
      }
    }, 10)
  }

  // Generate color hash for avatar
  const getAvatarColor = (name: string) => {
    const colors = ["#2563eb", "#7c3aed", "#059669", "#d97706", "#dc2626", "#0891b2"]
    let hash = 0
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash)
    return colors[Math.abs(hash) % colors.length]
  }

  return (
    <div className="position-relative w-100 mention-input-wrapper">
      <div className="d-flex align-items-center w-100 position-relative">
        {multiline ? (
          <textarea
            ref={inputRef as React.RefObject<HTMLTextAreaElement>}
            id={id}
            value={value}
            rows={rows}
            disabled={disabled}
            placeholder={placeholder}
            className={`form-control ${className}`}
            style={{ resize: "none", ...inputStyle }}
            autoFocus={autoFocus}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
          />
        ) : (
          <input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            id={id}
            type="text"
            value={value}
            disabled={disabled}
            placeholder={placeholder}
            className={`form-control ${className}`}
            style={inputStyle}
            autoFocus={autoFocus}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
          />
        )}

        {showQuickAtButton && (
          <button
            type="button"
            className="btn btn-sm btn-link text-muted position-absolute end-0 top-50 translate-middle-y me-1 p-1"
            title="Mencionar a un usuario (@)"
            disabled={disabled}
            onClick={triggerAtSymbol}
            style={{ zIndex: 3, opacity: 0.7 }}
          >
            <i className="fas fa-at" style={{ fontSize: "0.85rem" }} />
          </button>
        )}
      </div>

      {/* Floating Autocomplete Dropdown */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className="position-absolute shadow-lg border rounded-3 bg-white py-1 z-3"
          style={{
            bottom: "calc(100% + 6px)",
            left: 0,
            minWidth: "260px",
            maxWidth: "320px",
            maxHeight: "260px",
            overflowY: "auto",
            zIndex: 1060,
            borderColor: "rgba(0,0,0,0.12)",
          }}
        >
          {/* Header */}
          <div className="px-3 py-1.5 border-bottom bg-light d-flex align-items-center justify-content-between">
            <span className="small text-muted fw-semibold" style={{ fontSize: "0.72rem" }}>
              <i className="fas fa-at text-primary me-1" />
              Mencionar usuario
            </span>
            {mentionQuery && (
              <span className="badge bg-primary-subtle text-primary rounded-pill" style={{ fontSize: "0.65rem" }}>
                @{mentionQuery}
              </span>
            )}
          </div>

          {/* User List */}
          {filteredUsers.length === 0 ? (
            <div className="p-3 text-center text-muted small" style={{ fontSize: "0.78rem" }}>
              <i className="fas fa-user-slash d-block mb-1 text-secondary opacity-50" />
              No se encontraron usuarios
            </div>
          ) : (
            filteredUsers.map((user, idx) => {
              const isSelected = idx === selectedIndex
              const avatarColor = getAvatarColor(user.name)
              const initials = user.name
                .split(" ")
                .filter(Boolean)
                .slice(0, 2)
                .map((n) => n[0].toUpperCase())
                .join("")

              return (
                <div
                  key={user.id}
                  className={`px-3 py-2 d-flex align-items-center gap-2 cursor-pointer transition-colors ${
                    isSelected ? "bg-primary text-white" : "hover-bg-light"
                  }`}
                  style={{
                    cursor: "pointer",
                    backgroundColor: isSelected ? "#0d6efd" : "transparent",
                    transition: "background-color 0.1s ease",
                  }}
                  onMouseEnter={() => setSelectedIndex(idx)}
                  onMouseDown={(e) => {
                    e.preventDefault()
                    handleSelectUser(user)
                  }}
                >
                  {/* Avatar */}
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold flex-shrink-0"
                    style={{
                      width: "28px",
                      height: "28px",
                      fontSize: "0.72rem",
                      backgroundColor: isSelected ? "rgba(255,255,255,0.25)" : avatarColor,
                    }}
                  >
                    {initials || "U"}
                  </div>

                  {/* Name and Role */}
                  <div className="flex-grow-1 text-truncate" style={{ lineHeight: 1.2 }}>
                    <div
                      className={`fw-semibold text-truncate ${isSelected ? "text-white" : "text-dark"}`}
                      style={{ fontSize: "0.82rem" }}
                    >
                      {user.name}
                    </div>
                    {user.role && (
                      <div
                        className={`text-truncate ${isSelected ? "text-white-50" : "text-muted"}`}
                        style={{ fontSize: "0.7rem" }}
                      >
                        {user.role}
                      </div>
                    )}
                  </div>

                  {isSelected && (
                    <i className="fas fa-arrow-turn-down text-white opacity-75" style={{ fontSize: "0.7rem" }} />
                  )}
                </div>
              )
            })
          )}
        </div>
      )}
    </div>
  )
}

export default MentionInput
